<template>
    <div
        class="forms__field"
        :class="[
            { 'forms__field--file': ['file'].indexOf(field.type) !== -1 },
            { 'forms__field--group': ['group'].indexOf(field.type) !== -1 },
            { 'forms__field--inline': field.inline },
            { 'forms__field--flex': !field.inline },
            { 'forms__field--fluid': !field.width },
            `forms__field--${field.type}`,
            `forms__field--${props.size}`,
            field.wrapperClass,
        ]"
        :style="`width: ${field.width};`"
    >
        <div
            v-if="labelPosition === 'left'"
            class="forms__field_label_wrapper--left"
            :class="[`forms__field_label_wrapper--${size}`]"
        >
            <Label :for="elementId" :value="field.name" class="forms__field_label__label" :size="size" />
            <div v-if="field.has_tooltip && field.tooltip" class="forms__tooltip_button group">
                <Icon icon="hero/outline/question-mark-circle" class="w-4 h-4" />
                <div class="forms__tooltip">
                    <!-- eslint-disable -->
                    <p v-html="field.tooltip"></p>
                    <!-- eslint-enable -->
                </div>
            </div>
            <div
                v-if="field.has_foreign_id"
                v-tooltip="{ message: lang('fields::fields.field-editor.foreign_id'), position: 'right' }"
                class="forms__field_foreign-id"
            >
                ({{ field.foreign_id }})
            </div>
        </div>
        <div
            class="forms__field_input_wrapper"
            :class="[
                {
                    'overflow-hidden': ['file', 'attachment'].indexOf(field.type) !== -1,
                },
            ]"
        >
            <div
                v-if="labelPosition === 'top' && field.name"
                class="forms__field_label_wrapper--top"
                :class="[`forms__field_label_wrapper--${size}`]"
            >
                <Label :for="elementId" :value="field.name" :size="size" />
            </div>

            <!-- Slot: Before -->
            <slot name="field-before"></slot>

            <template v-if="['select', 'ajax', 'relation'].indexOf(field.type) !== -1">
                <component
                    :is="getAdminComponent(field)"
                    :id="elementId"
                    ref="fieldInstance"
                    v-model="modelValue"
                    :field="field"
                    :type="field.type"
                    :size="size"
                    :invalid="isInvalid"
                    :class="[{ 'border-red-600': $page.props.errors[field.slug] }, `forms__field_input--${field.type}`]"
                    :autocomplete="$attrs.autocomplete || field.slug"
                    :dusk="dusk || `form-${field.slug}`"
                    :required="required"
                    :placeholder="props.placeholder ?? field.placeholder"
                    :tabindex="tabindex"
                    :readonly="!!(field.readonly ?? props.readonly)"
                    :disabled="!!(field.disabled ?? props.disabled)"
                    :checked="numberToBoolean(modelValue)"
                    :options="options && options.length > 0 ? options : field.options"
                    :colors="options && options.length > 0 ? options : field.options"
                    :form="props.form"
                    :show-hide-bar="field.showHideBar"
                    :height="field.height"
                    :file="file"
                    :multiple="multiple || numberToBoolean(field.multiple)"
                    :orderable="field.orderable ?? false"
                    :is-featured="field.isFeatured ?? false"
                    :upload-disk="field.uploadDisk ?? null"
                    :upload-directory="field.uploadDirectory ?? ''"
                    :source="props.source ?? (field && field.source ? field.source : null)"
                    :preload="preload || (field && field.preload ? field.preload : false)"
                    :create="create"
                    :slugify-from-source="slugifyFromSource"
                    :forbidden="props.forbidden ?? (field && field.forbidden ? field.forbidden : [])"
                    :types="props.types"
                    :blocks="props.blocks"
                    :view-options="viewOptions"
                    :progress="props.progress"
                    :icon_preview="props.icon_preview"
                    :icon_color="props.icon_color"
                    :alt="props.alt"
                    :accept="props.accept ?? (field && field.accept ? field.accept : '')"
                    :recommended="props.recommended ?? (field && field.recommended ? field.recommended : {})"
                    :searchable="props.searchable || (field && field.searchable ? field.searchable : false)"
                    :params="props.params"
                    :interactive="props.interactive"
                    :actions="props.actions"
                    :ajax-route="props.ajaxRoute"
                    :load-current="props.loadCurrent"
                    :minus-icon="props.minusIcon"
                    :plus-icon="props.plusIcon"
                    :repeater-element="props.repeaterElement"
                    :portal="props.portal || field?.portal || false"
                    @update:set-featured="emit('update:setFeatured', $event)"
                    @update:checked="emit('update:checked', $event)"
                    @cropped="emit('cropped', $event)"
                    @blur="emit('blur', $event)"
                    @focus="emit('focus', $event)"
                    @change="emit('change', $event)"
                    @repeater-change="emit('repeaterChange', $event)"
                    @keyup="emit('keyup', $event)"
                    @keydown="emit('keydown', $event)"
                    @update:model-value="emit('update:modelValue', $event)"
                    @processing:start="emit('processing:start', $event)"
                    @processing:end="emit('processing:end', $event)"
                >
                    <template #currentValue="slotProps">
                        <slot name="currentValue" v-bind="slotProps"></slot>
                    </template>
                    <template #selectedItem="slotProps">
                        <slot name="selectedItem" v-bind="slotProps"></slot>
                    </template>
                    <template #option="slotProps">
                        <slot name="option" v-bind="slotProps"></slot>
                    </template>
                    <template #optionsBefore="slotProps">
                        <slot name="optionsBefore" v-bind="slotProps"></slot>
                    </template>
                    <template #optionsAfter="slotProps">
                        <slot name="optionsAfter" v-bind="slotProps"></slot>
                    </template>
                </component>
                <FieldErrors v-if="$page.props.errors[field.slug]" :errors="$page.props.errors[field.slug]" />
                <div v-else-if="$slots.help || field.help" class="forms__field_help">
                    <slot name="help">{{ field.help }}</slot>
                </div>
            </template>
            <template v-else-if="['repeater', 'group', 'fieldEditor'].indexOf(field.type) !== -1">
                <component
                    :is="getAdminComponent(field)"
                    :id="elementId"
                    ref="fieldInstance"
                    v-model="modelValue"
                    :field="field"
                    :type="field.type"
                    :size="size"
                    :invalid="isInvalid"
                    :class="[{ 'border-red-600': $page.props.errors[field.slug] }, `forms__field_input--${field.type}`]"
                    :autocomplete="$attrs.autocomplete || field.slug"
                    :dusk="dusk || `form-${field.slug}`"
                    :required="required"
                    :placeholder="props.placeholder ?? field.placeholder"
                    :tabindex="tabindex"
                    :readonly="!!(field.readonly ?? props.readonly)"
                    :disabled="!!(field.disabled ?? props.disabled)"
                    :checked="numberToBoolean(modelValue)"
                    :options="options && options.length > 0 ? options : field.options"
                    :colors="options && options.length > 0 ? options : field.options"
                    :form="props.form"
                    :show-hide-bar="field.showHideBar"
                    :height="field.height"
                    :file="file"
                    :multiple="multiple || numberToBoolean(field.multiple)"
                    :orderable="field.orderable ?? false"
                    :is-featured="field.isFeatured ?? false"
                    :upload-disk="field.uploadDisk ?? null"
                    :upload-directory="field.uploadDirectory ?? ''"
                    :source="props.source ?? (field && field.source ? field.source : null)"
                    :preload="preload || (field && field.preload ? field.preload : false)"
                    :create="create"
                    :slugify-from-source="slugifyFromSource"
                    :forbidden="props.forbidden ?? (field && field.forbidden ? field.forbidden : [])"
                    :types="props.types"
                    :blocks="props.blocks"
                    :view-options="viewOptions"
                    :progress="props.progress"
                    :icon_preview="props.icon_preview"
                    :icon_color="props.icon_color"
                    :alt="props.alt"
                    :accept="props.accept ?? (field && field.accept ? field.accept : '')"
                    :recommended="props.recommended"
                    :label-position="props.labelPosition"
                    :params="props.params"
                    :interactive="props.interactive"
                    :actions="props.actions"
                    :ajax-route="props.ajaxRoute"
                    :load-current="props.loadCurrent"
                    :minus-icon="props.minusIcon"
                    :plus-icon="props.plusIcon"
                    :repeater-element="props.repeaterElement"
                    @update:set-featured="emit('update:setFeatured', $event)"
                    @update:checked="emit('update:checked', $event)"
                    @cropped="emit('cropped', $event)"
                    @blur="emit('blur', $event)"
                    @focus="emit('focus', $event)"
                    @change="emit('change', $event)"
                    @repeater-change="emit('repeaterChange', $event)"
                    @keyup="emit('keyup', $event)"
                    @keydown="emit('keydown', $event)"
                    @update:model-value="emit('update:modelValue', $event)"
                    @processing:start="emit('processing:start', $event)"
                    @processing:end="emit('processing:end', $event)"
                >
                    <template
                        v-for="element in modelValue"
                        :key="`type-${element.type}`"
                        #[`repeater-after-type-${element?.type}`]="slotProps"
                    >
                        <slot :name="`repeater-after-type-${element?.type}`" v-bind="slotProps"></slot>
                    </template>
                    <template
                        v-for="element in modelValue"
                        :key="`slug-${element.slug}`"
                        #[`repeater-after-slug-${element?.slug}`]="slotProps"
                    >
                        <slot :name="`repeater-after-slug-${element?.slug}`" v-bind="slotProps"></slot>
                    </template>
                </component>
                <FieldErrors v-if="$page.props.errors[field.slug]" :errors="$page.props.errors[field.slug]" />
                <div v-else-if="$slots.help || field.help" class="forms__field_help">
                    <slot name="help">{{ field.help }}</slot>
                </div>
            </template>
            <template v-else>
                <div class="forms__field_input_row">
                    <component
                        :is="getAdminComponent(field)"
                        :id="elementId"
                        ref="fieldInstance"
                        v-model="modelValue"
                        :field="field"
                        :type="field.type"
                        :size="size"
                        :invalid="isInvalid"
                        :class="[defaultBranchInputClass, `forms__field_input--${field.type}`]"
                        :autocomplete="$attrs.autocomplete || field.slug"
                        :dusk="dusk || `form-${field.slug}`"
                        :required="required"
                        :placeholder="props.placeholder ?? field.placeholder"
                        :tabindex="tabindex"
                        :readonly="!!(field.readonly ?? props.readonly)"
                        :disabled="!!(field.disabled ?? props.disabled)"
                        :checked="numberToBoolean(modelValue)"
                        :options="options && options.length > 0 ? options : field.options"
                        :colors="options && options.length > 0 ? options : field.options"
                        :form="props.form"
                        :show-hide-bar="field.showHideBar"
                        :height="field.height"
                        :file="file"
                        :multiple="multiple || numberToBoolean(field.multiple)"
                        :orderable="field.orderable ?? false"
                        :is-featured="field.isFeatured ?? false"
                        :upload-disk="field.uploadDisk ?? null"
                        :upload-directory="field.uploadDirectory ?? ''"
                        :source="props.source ?? (field && field.source ? field.source : null)"
                        :preload="preload || (field && field.preload ? field.preload : false)"
                        :create="create"
                        :slugify-from-source="slugifyFromSource"
                        :forbidden="props.forbidden ?? (field && field.forbidden ? field.forbidden : [])"
                        :types="props.types"
                        :blocks="props.blocks"
                        :view-options="viewOptions"
                        :progress="props.progress"
                        :icon_preview="props.icon_preview"
                        :icon_color="props.icon_color"
                        :alt="props.alt"
                        :accept="props.accept ?? (field && field.accept ? field.accept : '')"
                        :recommended="props.recommended"
                        :searchable="props.searchable || (field && field.searchable ? field.searchable : false)"
                        :label-position="props.labelPosition"
                        :params="props.params"
                        :interactive="props.interactive"
                        :actions="props.actions"
                        :ajax-route="props.ajaxRoute"
                        :load-current="props.loadCurrent"
                        :use-s3-for-media="props.params?.useS3ForMedia ?? false"
                        :minus-icon="props.minusIcon"
                        :plus-icon="props.plusIcon"
                        :repeater-element="props.repeaterElement"
                        @update:set-featured="emit('update:setFeatured', $event)"
                        @update:checked="emit('update:checked', $event)"
                        @cropped="emit('cropped', $event)"
                        @blur="emit('blur', $event)"
                        @focus="emit('focus', $event)"
                        @change="emit('change', $event)"
                        @repeater-change="emit('repeaterChange', $event)"
                        @keyup="emit('keyup', $event)"
                        @keydown="emit('keydown', $event)"
                        @update:model-value="emit('update:modelValue', $event)"
                        @processing:start="emit('processing:start', $event)"
                        @processing:end="emit('processing:end', $event)"
                    />
                    <div v-if="showInvalidIcon" class="forms__field_input_invalid_icon">
                        <Icon icon="hero/outline/exclamation-circle" class="w-4 h-4" />
                    </div>
                </div>
                <FieldErrors v-if="$page.props.errors[field.slug]" :errors="$page.props.errors[field.slug]" />
                <div v-else-if="$slots.help || field.help" class="forms__field_help">
                    <slot name="help">{{ field.help }}</slot>
                </div>
            </template>

            <!-- Slot: After -->
            <slot name="field-after"></slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, toRefs, watch, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import type { FieldComponent, Field } from '@src/Common/Components/types';
import FieldErrors from '@src/Common/Components/FieldErrors.vue';

type SelectedValue = null | string | number | object | string[] | number[] | object[];

const emit = defineEmits<{
    (event: 'change', content: SelectedValue): void;
    (event: 'repeaterChange', content: any): void;
    (event: 'focus', content: any): void;
    (event: 'blur', content: any): void;
    (event: 'cropped', content: any): void;
    (event: 'update:setFeatured', content: number): void;
    (event: 'update:checked', content: string): void;
    (event: 'update:modelValue', content: any): void;
    (event: 'processing:start', content: any): void;
    (event: 'processing:end', content: any): void;
    (event: 'keyup', content: KeyboardEvent): void;
    (event: 'keydown', content: KeyboardEvent): void;
}>();

const modelValue = defineModel({
    type: [String, Number, Boolean, Array, Object],
});

const props = withDefaults(defineProps<FieldComponent>(), {
    field: () => ({
        name: '',
        slug: '',
        type: 'text',
    }),
    size: 'base',
    required: false,
    placeholder: null,
    multiple: false,
    preload: false,
    create: false,
    searchable: false,
    options: () => [],
    file: null,
    labelPosition: 'left',
    dusk: null,
    tabindex: null,
    step: null,
    rows: null,
    errors: () => {},
    inline: false,
    id: null,
    source: null,
    slugifyFromSource: true,
    forbidden: () => [],
    readonly: false,
    parent: () => {},
    form: () => {},
    types: null,
    blocks: null,
    viewOptions: null,
    progress: 0,
    icon_preview: 'hero/outline/document',
    icon_color: 'gray',
    alt: null,
    accept: null,
    interactive: true,
    ajaxRoute: null,
    loadCurrent: true,
    minusIcon: null,
    plusIcon: null,
    repeaterElement: null,
    recommended: () => ({
        aspect: null,
        width: null,
        height: null,
        sizes: null,
    }),
    params: () => ({}),
    actions: () => [],
});

if (props.field.type === 'builder' && (typeof modelValue.value === 'undefined' || modelValue.value === null)) {
    modelValue.value = [];
}

const {
    field,
    required,
    multiple,
    options,
    size,
    file,
    labelPosition,
    tabindex,
    preload,
    create,
    parent,
    viewOptions,
} = toRefs(props);

const fieldInstance = ref(null);

const isInvalid = computed(() => {
    const errors = usePage().props.errors;
    return typeof errors[field.value.slug] !== 'undefined';
});

const SIMPLE_INPUT_TYPES = ['text', 'email', 'password', 'tel', 'number', 'search'];

const showInvalidIcon = computed(() => {
    return isInvalid.value && SIMPLE_INPUT_TYPES.includes(field.value.type);
});

const defaultBranchInputClass = computed(() => {
    if (!isInvalid.value) return '';
    return showInvalidIcon.value ? 'border-red-600 pr-8' : 'border-red-600';
});

// TODO: Find out if this is needed, does not look
watch(props, (value) => {
    if (modelValue.value !== value.modelValue) {
        modelValue.value = value.modelValue;
    }
});

const numberToBoolean = (input: number | boolean) => {
    if (typeof input === 'boolean') {
        return input;
    }

    if (typeof input === 'string') {
        return input === '1';
    }

    return input === 1;
};

const getElementIdWithParents = (field: FieldComponent, parent: FieldComponent | null | undefined) => {
    if (parent) {
        return `${getElementIdWithParents(parent.field, parent.parent)}-${field.id ?? field.slug}`;
    }

    if (!props.id && !field.slug) {
        // Generate random id
        return `field-${Math.random().toString(36).substring(2, 15)}`;
    }

    return `${props.id ?? field.slug}`;
};

const elementId = computed(() => getElementIdWithParents(field?.value, parent?.value));

/**
 * Get the admin component for the field,
 * this is used to provide adminComponent
 * for old style of fields.
 *
 * @param field Field
 * @returns string AdminComponent
 */
const getAdminComponent = (field: Field) => {
    if (field.adminComponent && field.adminComponent !== '') {
        return field.adminComponent;
    }

    switch (field.type) {
        case 'text':
            return 'AdminText';

        case 'file':
        case 'attachment':
            return 'AdminFile';

        case 'ajax':
            return 'AdminAjax';

        case 'color':
            return 'AdminColor';

        case 'date':
            return 'AdminDate';

        case 'datetime':
            return 'AdminDatetime';

        case 'time':
            return 'AdminTime';

        case 'wysiwyg':
            return 'AdminWYSIWYG';

        case 'html':
            return 'AdminHTML';

        case 'textarea':
            return 'AdminTextarea';

        case 'email':
            return 'AdminEmail';

        case 'password':
            return 'AdminPassword';

        case 'number':
            return 'AdminNumber';

        case 'select':
            return 'AdminSelect';

        case 'boolean':
        case 'checkbox':
            return 'AdminCheckbox';

        case 'radio':
            return 'AdminRadio';

        case 'relation':
            return 'AdminRelation';

        case 'price_cents':
            return 'AdminPriceCents';

        case 'markdown':
            return 'AdminMarkdown';

        case 'message':
            return 'AdminMessage';

        case 'show_link':
            return 'ShowLink';

        case 'show_text':
            return 'ShowText';

        case 'builder':
            return 'Builder';

        case 'repeater':
            return 'AdminRepeater';

        case 'group':
            return 'AdminFieldGroup';

        case 'search':
            return 'AdminSearch';

        case 'table':
            return 'AdminTable';

        case 'table_repeater':
            return 'AdminTableRepeater';

        case 'fieldEditor':
            return 'FieldEditor';

        case 'select-icon':
            return 'AdminSelectIcon';

        case 'copy-to-clipboard':
            return 'CopyToClipboard';
    }

    return 'AdminText';
};

/**
 * Expose
 */
defineExpose({
    fieldInstance,
});
</script>
